import { Fragment, useCallback, useState, useMemo } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Typography,
  MenuItem,
  MenuList,
  Popover,
} from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { fCurrencyFormatKeepZero } from 'src/utils/format-number';
import { fConvertDateFormat } from 'src/utils/format-date';
import { AgingBucket, IAgingProducer } from 'src/types';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { TableHeadCustom, type TableHeadCellProps, DataTableContainer } from 'src/components/table';
import { StatsExportButton } from 'src/components/stats-export';
import AgingBucketBadge from '../aging-bucket-badge';
import { AGING_BUCKET_CONFIG } from '../aging-bucket-config';
import { producerAgingWorkbook } from './producer-aging-export';

type SortField = 'producerName' | 'totalOverdue' | 'invoiceCount';
type SortOrder = 'asc' | 'desc';

type Props = {
  producers: IAgingProducer[];
  activeBucket: AgingBucket | null;
  /** The page (and section) the table sits on, in the viewer's language: the export's title. */
  exportTitle: string;
};

export default function ProducerAgingTable({ producers, activeBucket, exportTitle }: Props) {
  const { t, currentLang } = useLocales('operativaDashboard');
  const { t: tCommon } = useLocales();
  const router = useRouter();

  const [expandedProducerId, setExpandedProducerId] = useState<number | null>(null);
  const [sortField, setSortField] = useState<SortField>('totalOverdue');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuProducerSlug, setMenuProducerSlug] = useState<string | null>(null);

  const TABLE_HEAD: TableHeadCellProps[] = useMemo(
    () => [
      { field: 'expand', width: 40 },
      { field: 'producerName', title: t('produttore') },
      { field: 'totalOverdue', title: t('totale_dovuto'), align: 'right' },
      { field: 'worstBucket', title: t('bucket_peggiore') },
      { field: 'invoiceCount', title: t('n_ordini'), align: 'right' },
      { field: 'actions', title: t('azioni'), align: 'right' },
    ],
    [t],
  );

  const filteredProducers = useMemo(() => {
    if (!producers?.length) return [];
    let result = producers;
    if (activeBucket) {
      result = producers.filter((p) => p.worstBucket === activeBucket);
    }
    return [...result].sort((a, b) => {
      const multiplier = sortOrder === 'asc' ? 1 : -1;
      if (sortField === 'producerName')
        return multiplier * a.producerName.localeCompare(b.producerName);
      if (sortField === 'totalOverdue') return multiplier * (a.totalOverdue - b.totalOverdue);
      if (sortField === 'invoiceCount') return multiplier * (a.invoiceCount - b.invoiceCount);
      return 0;
    });
  }, [producers, activeBucket, sortField, sortOrder]);

  // NAT-2481 — the export is the table as it stands: the bucket's producers, in the screen's order.
  const buildExport = useCallback(async () => {
    const sortTitle = TABLE_HEAD.find((h) => h.field === sortField)?.title ?? '';
    return producerAgingWorkbook({
      producers: filteredProducers,
      page: exportTitle,
      bucketLabel: activeBucket ? t(AGING_BUCKET_CONFIG[activeBucket].labelKey) : null,
      sortLabel: `${sortTitle} (${t(sortOrder === 'asc' ? 'export_crescente' : 'export_decrescente')})`,
      t: (k, o) => (k === 'export_esportato_il' ? tCommon(k, o) : t(k, o)),
    });
  }, [TABLE_HEAD, sortField, sortOrder, filteredProducers, exportTitle, activeBucket, t, tCommon]);

  const handleSort = (field: string) => {
    const sortable: string[] = ['producerName', 'totalOverdue', 'invoiceCount'];
    if (!sortable.includes(field)) return;
    const typed = field as SortField;
    if (sortField === typed) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(typed);
      setSortOrder('desc');
    }
  };

  const handleToggleExpand = (producerId: number) => {
    setExpandedProducerId(expandedProducerId === producerId ? null : producerId);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, producerSlug: string) => {
    setMenuAnchor(event.currentTarget);
    setMenuProducerSlug(producerSlug);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuProducerSlug(null);
  };

  return (
    <Card>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5 }}>
        <StatsExportButton build={buildExport} disabled={!filteredProducers.length} />
      </Box>
      <DataTableContainer>
        <Table stickyHeader size="small">
          <TableHeadCustom
            order={sortOrder}
            orderBy={sortField}
            headLabel={TABLE_HEAD}
            onSort={handleSort}
          />
          <TableBody>
            {filteredProducers.map((producer) => {
              const isExpanded = expandedProducerId === producer.producerId;

              return (
                <Fragment key={producer.producerId}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleToggleExpand(producer.producerId)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        <Iconify
                          icon={
                            isExpanded ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'
                          }
                          width={16}
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            router.push(paths.admin.producers.details(producer.producerSlug));
                          }}
                        >
                          {producer.producerName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {producer.producerSlug}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                        {producer.totalOverdue === null || producer.totalOverdue === undefined
                          ? '—'
                          : fCurrencyFormatKeepZero(producer.totalOverdue, currentLang.value)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <AgingBucketBadge bucket={producer.worstBucket} />
                    </TableCell>
                    <TableCell align="right">{producer.invoiceCount}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, producer.producerSlug);
                        }}
                      >
                        <Iconify icon="eva:more-vertical-fill" width={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, pl: 6, backgroundColor: 'background.neutral' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'fontWeightBold' }}>
                                  {t('codice_ordine')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'fontWeightBold' }} align="right">
                                  {t('importo')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'fontWeightBold' }}>
                                  {t('data_scadenza')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'fontWeightBold' }} align="right">
                                  {t('giorni_ritardo')}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(producer.invoices ?? []).map((order) => (
                                <TableRow key={order.orderCode}>
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                      onClick={() =>
                                        router.push(
                                          paths.admin.orderTypes.producerOrders.details(
                                            order.orderCode,
                                          ),
                                        )
                                      }
                                    >
                                      {order.orderCode}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {fCurrencyFormatKeepZero(order.amountDue, currentLang.value)}
                                  </TableCell>
                                  <TableCell>{fConvertDateFormat(order.dueDate)}</TableCell>
                                  <TableCell align="right">{order.daysOverdue}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </DataTableContainer>

      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* v9 MenuItems require MenuListContext; a bare Popover does not provide it. */}
        <MenuList>
          <MenuItem
            onClick={() => {
              if (menuProducerSlug) {
                router.push(paths.admin.producers.details(menuProducerSlug));
              }
              handleMenuClose();
            }}
          >
            <Iconify icon="eva:eye-fill" width={20} sx={{ mr: 1 }} />
            {t('vedi_ordini')}
          </MenuItem>
        </MenuList>
      </Popover>
    </Card>
  );
}
